////////////////////////////////////////////////////////////////////////////////
//                                                                            //
//  IRONSIDE - Public Website GUI                                             //
//                                                                            //
//  Version: 1.0.3                                                            //
//  Summary: News listing route — GET /public/news, paginated with "Load more" //
//                                                                            //
////////////////////////////////////////////////////////////////////////////////

import { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router';
import { motion } from 'framer-motion';

import { usePageTitle } from '@/hooks/usePageTitle';
import { useReducedMotion } from '@/hooks/useReducedMotion';
import api from '@/lib/api';
import { formatDateShort } from '@/lib/format';
import { cardHover, cardHoverImage, clipWipe, reduceHover, reduceVariants } from '@/lib/motion';
import type { NewsListItem, NewsListResponse } from '@/types/public';
import { Spinner } from '@/components/feedback/Spinner';
import { ErrorState } from '@/components/feedback/ErrorState';
import { EmptyState } from '@/components/feedback/EmptyState';
import { Img } from '@/components/media/Img';
import { PageHero } from '@/components/content/PageHero';
import { RevealGroup, RevealItem } from '@/components/content/Reveal';

const PAGE_SIZE = 10;

type LoadStatus = 'loading' | 'success' | 'loading-more' | 'error';

export function News () {
  usePageTitle('News');

  const reducedMotion = useReducedMotion();
  const [items, setItems] = useState<NewsListItem[]>([]);
  const [page, setPage] = useState(1);
  const [numPages, setNumPages] = useState(1);
  const [total, setTotal] = useState<number | null>(null);
  const [status, setStatus] = useState<LoadStatus>('loading');
  const [, setError] = useState<Error | null>(null);
  const [reloadKey, setReloadKey] = useState(0);

  // Initial load — a genuine subscription to the network on mount (and on retry).
  // "Load more" is a user-initiated event, not a re-run of this effect, so it lives
  // in its own handler below rather than being folded into a `page` dependency here.
  useEffect(() => {
    const controller = new AbortController();
    // eslint-disable-next-line react-hooks/set-state-in-effect -- resetting to the loading state before issuing a new request IS the synchronisation this effect performs (a fresh `reloadKey` is new external-system state to reflect); the actual subscription is the .then/.catch pair below. Mirrors hooks/useApi.ts's identical pattern.
    setStatus('loading');
    setError(null);

    api
      .get<NewsListResponse>('/public/news', { params: { page: 1, limit: PAGE_SIZE }, signal: controller.signal })
      .then((response) => {
        setItems(response.data.items);
        setPage(response.data.page);
        setNumPages(response.data.num_pages);
        setTotal(response.data.total);
        setStatus('success');
      })
      .catch((err: unknown) => {
        if (controller.signal.aborted) return;
        setError(err instanceof Error ? err : new Error('Request failed'));
        setStatus('error');
      });

    return () => controller.abort();
  }, [reloadKey]);

  const loadMore = useCallback(() => {
    const nextPage = page + 1;
    setStatus('loading-more');
    api
      .get<NewsListResponse>('/public/news', { params: { page: nextPage, limit: PAGE_SIZE } })
      .then((response) => {
        setItems((prev) => [...prev, ...response.data.items]);
        setPage(response.data.page);
        setNumPages(response.data.num_pages);
        setTotal(response.data.total);
        setStatus('success');
      })
      .catch((err: unknown) => {
        setError(err instanceof Error ? err : new Error('Request failed'));
        setStatus('error');
      });
  }, [page]);

  return (
    <div>
      <PageHero title="News & Updates" />

      <div className="mx-auto max-w-6xl px-6 py-16">
        {status === 'loading' && <div className="mt-10"><Spinner label="Loading news" /></div>}

        {status === 'error' && (
          <div className="mt-10">
            <ErrorState message="News couldn't be loaded right now." onRetry={() => setReloadKey((k) => k + 1)} />
          </div>
        )}

        {status !== 'loading' && status !== 'error' && total === 0 && (
          <div className="mt-10"><EmptyState message="No news articles have been published yet." /></div>
        )}

        {items.length > 0 && (
          <>
            <RevealGroup as="ul" className="mt-10 grid grid-cols-1 gap-8 sm:grid-cols-2 lg:grid-cols-3">
              {items.map((item) => (
                <RevealItem key={item.slug} as="li">
                  {/* `.glass-card`, not <GlassTile> — see styles/glass.css: one
                      backdrop-filter per card across a paginated grid is the
                      stacking foot-gun §5's perf guardrail forbids. */}
                  <motion.div whileHover={reduceHover(cardHover, reducedMotion)}>
                    <div className="glass-card overflow-hidden">
                      {item.heroImage && (
                        <div className="overflow-hidden">
                          <motion.div
                            variants={reduceVariants(clipWipe, reducedMotion)}
                            whileHover={reduceHover(cardHoverImage, reducedMotion)}
                          >
                            <Img src={item.heroImage} alt={item.title} className="h-48 w-full object-cover" />
                          </motion.div>
                        </div>
                      )}
                      <div className="p-4">
                        <p className="text-sm text-muted">{formatDateShort(item.publishedAt)}{item.author ? ` · ${item.author}` : ''}</p>
                        <h2 className="mt-1 text-lg font-semibold text-ink">
                          <Link to={`/news/${item.slug}`} className="hover:underline">{item.title}</Link>
                        </h2>
                        {item.excerpt && <p className="mt-2 text-sm text-muted">{item.excerpt}</p>}
                      </div>
                    </div>
                  </motion.div>
                </RevealItem>
              ))}
            </RevealGroup>

            {page < numPages && (
              <div className="mt-10 text-center">
                <button
                  type="button"
                  onClick={loadMore}
                  disabled={status === 'loading-more'}
                  className="rounded border border-line px-6 py-2 text-ink disabled:opacity-50"
                >
                  {status === 'loading-more' ? 'Loading…' : 'Load more'}
                </button>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
}
